import { useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Zap, Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

/**
 * Soft paywall modal — surfaced inline whenever a free user hits a limit
 * (2nd class, 6th lesson plan, gated export, etc.).
 *
 * Open/close is controlled by the parent. Pass `open`, `onClose`, and either
 * `feature` (string) for a generic message, or override copy via `title/body`.
 */
export default function PaywallModal({
    open,
    onClose,
    feature,
    title,
    body,
}) {
    const { lang } = useLang();

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;

    const defaultTitle =
        lang === "es" ? "Sube a Teacher Pro" : "Upgrade to Teacher Pro";
    const defaultBody =
        lang === "es"
            ? feature
                ? `${feature} es una función de Teacher Pro. Empieza tu prueba gratis de 14 días, sin tarjeta.`
                : "Esta función requiere Teacher Pro. Empieza tu prueba gratis de 14 días, sin tarjeta."
            : feature
              ? `${feature} is a Teacher Pro feature. Start your 14-day free trial — no card required.`
              : "This feature requires Teacher Pro. Start your 14-day free trial — no card required.";

    return (
        <div
            className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
            data-testid="paywall-modal"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose?.();
            }}
        >
            <div className="card p-7 max-w-md w-full relative border-l-2 border-l-[var(--lime)]">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-[var(--muted)] hover:text-white"
                    data-testid="paywall-close"
                    aria-label="Close"
                >
                    <X size={18} />
                </button>
                <span className="tag inline-flex items-center gap-1">
                    <Sparkles size={12} className="text-[var(--lime)]" />
                    {lang === "es" ? "// solo en pro" : "// pro only"}
                </span>
                <h2
                    className="font-display font-black text-white text-2xl uppercase mt-3 leading-tight"
                    data-testid="paywall-title"
                >
                    {title || defaultTitle}
                </h2>
                <p
                    className="font-mono text-sm text-[var(--muted)] mt-3"
                    data-testid="paywall-body"
                >
                    {body || defaultBody}
                </p>
                <div className="flex flex-col gap-2 mt-6">
                    <Link
                        to="/pricing"
                        onClick={onClose}
                        className="font-mono text-xs uppercase tracking-[0.22em] bg-white text-black px-5 py-3 hover:bg-[var(--lime)] inline-flex items-center justify-center gap-2"
                        data-testid="paywall-cta-pricing"
                    >
                        <Zap size={14} />
                        {lang === "es" ? "Ver planes" : "See plans"}
                    </Link>
                    <button
                        type="button"
                        onClick={onClose}
                        className="font-mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)] hover:text-white py-2"
                        data-testid="paywall-cta-dismiss"
                    >
                        {lang === "es" ? "Ahora no" : "Not now"}
                    </button>
                </div>
            </div>
        </div>
    );
}
